import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SimpleDateField.css";

function pad(n) {
  return String(n).padStart(2, "0");
}
function fmt(date) {
  if (!date) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

function sameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function stripTime(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function inRange(d, min, max) {
  if (!d) return false;
  const dt = stripTime(d).getTime();
  if (min && dt < stripTime(min).getTime()) return false;
  if (max && dt > stripTime(max).getTime()) return false;
  return true;
}

// Build a 6x7 grid (Sun–Sat) for the visible month
function buildCalendarGrid(
  viewDate,
  { minDate, maxDate, disableWeekends, disablePastDates }
) {
  const start = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const startWeekday = start.getDay();
  const gridStart = new Date(start);
  gridStart.setDate(start.getDate() - startWeekday);
  const today = stripTime(new Date());
  const cells = [];

  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    const outOfRange =
      (minDate && stripTime(d) < stripTime(minDate)) ||
      (maxDate && stripTime(d) > stripTime(maxDate)) ||
      (disablePastDates && stripTime(d) < today); // <-- NEW CONDITION

    const disabled = outOfRange || (disableWeekends && isWeekend);
    cells.push({
      date: d,
      inMonth: d.getMonth() === viewDate.getMonth(),
      disabled,
      weekend: isWeekend,
    });
  }
  return cells;
}

export default function SimpleDateField({
  label = "Date",
  placeholder = "YYYY-MM-DD",
  labelPosition = "top-left",
  wrapperClassName = "",
  inputClassName = "",
  id,
  value,
  onChange = () => {},
  minDate,
  maxDate,
  disabled = false,
  error = "",
  helperText = "",
  disableWeekends = false,
  disablePastDates = false, // <-- NEW PROP
  customWidth = "7rem",
  showBelow=false,
  ...rest
}) {
  const inputId = id || `sdf-input-${Math.random().toString(36).slice(2, 9)}`;
  const parsedValue = useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return value;

    const s = String(value).trim();
    // Accept 2025-08-19 (preferred) or 2025/08/19 (legacy)
    const m = /^(\d{4})[-\/](\d{2})[-\/](\d{2})$/.exec(s);
    if (!m) return null;

    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }, [value]);

  const today = stripTime(new Date());
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() =>
    parsedValue
      ? new Date(parsedValue.getFullYear(), parsedValue.getMonth(), 1)
      : new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [activeDate, setActiveDate] = useState(parsedValue || today);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);

  const grid = useMemo(
    () =>
      buildCalendarGrid(viewDate, {
        minDate,
        maxDate,
        disableWeekends,
        disablePastDates,
      }),
    [viewDate, minDate, maxDate, disableWeekends, disablePastDates]
  );

  // Close on outside click
  useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Keep view in sync when value changes
  useEffect(() => {
    if (parsedValue) {
      setViewDate(
        new Date(parsedValue.getFullYear(), parsedValue.getMonth(), 1)
      );
      setActiveDate(parsedValue);
    }
  }, [parsedValue]);

  function commit(d) {
    if (!inRange(d, minDate, maxDate)) return;
    if (disableWeekends && (d.getDay() === 0 || d.getDay() === 6)) return;
    if (disablePastDates && stripTime(d) < stripTime(new Date())) return; // <-- NEW GUARD
    onChange(stripTime(d));
    setOpen(false);
    btnRef.current?.focus();
  }

  function moveActive(days) {
    const next = new Date(activeDate);
    next.setDate(activeDate.getDate() + days);
    setActiveDate(next);
    if (
      next.getMonth() !== viewDate.getMonth() ||
      next.getFullYear() !== viewDate.getFullYear()
    ) {
      setViewDate(new Date(next.getFullYear(), next.getMonth(), 1));
    }
  }

  function onKeyDown(e) {
    if (!open) {
      if (["Enter", " ", "ArrowDown"].includes(e.key) && !disabled) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      commit(activeDate);
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      moveActive(-1);
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      moveActive(1);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-7);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(7);
      return;
    }
    if (e.key === "PageUp") {
      e.preventDefault();
      setViewDate(addMonths(viewDate, -1));
      return;
    }
    if (e.key === "PageDown") {
      e.preventDefault();
      setViewDate(addMonths(viewDate, 1));
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      setActiveDate(new Date(viewDate.getFullYear(), viewDate.getMonth(), 1));
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      setActiveDate(
        new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0)
      );
      return;
    }
  }

  return (
    <div
      ref={wrapRef}
      className={[
        "stf-wrap",
        `stf-label-pos-${labelPosition}`,
        "sdf-wrap",
        wrapperClassName,
        disabled ? "is-disabled" : "",
        error ? "is-error" : "",
      ].join(" ")}
      style={{ width: customWidth || "100%", maxWidth: customWidth || "100%" }}
      onKeyDown={onKeyDown}
    >
      <label htmlFor={inputId} className="stf-label">
        {label}
      </label>

      <div className={["sdf-field", inputClassName].join(" ")}>
        <button
          id={inputId}
          ref={btnRef}
          type="button"
          className="sdf-button"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
          {...rest}
        >
          <span
            className={["sdf-text", parsedValue ? "" : "is-placeholder"].join(
              " "
            )}
          >
            {parsedValue ? fmt(parsedValue) : placeholder}
          </span>
          <span className="sdf-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect
                x="3"
                y="5"
                width="18"
                height="16"
                rx="3"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M16 3v4M8 3v4M3 10h18"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </span>
        </button>

        {open && (
          <div className="sdf-popover" style={{ top: showBelow ? "0rem" : undefined, bottom: showBelow ? undefined : "0rem" }}  role="dialog" aria-label="Choose date">
            <div className="sdf-head">
              <button
                type="button"
                className="sdf-nav"
                onClick={() => setViewDate(addMonths(viewDate, -1))}
                aria-label="Previous month"
              >
                ‹
              </button>
              <div className="sdf-title">
                {viewDate.toLocaleString(undefined, {
                  month: "long",
                  year: "numeric",
                })}
              </div>
              <button
                type="button"
                className="sdf-nav"
                onClick={() => setViewDate(addMonths(viewDate, 1))}
                aria-label="Next month"
              >
                ›
              </button>
            </div>

            <div className="sdf-grid">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="sdf-dow" aria-hidden>
                  {d}
                </div>
              ))}
              {grid.map(({ date, inMonth, disabled: dis, weekend }, i) => {
                const isSelected = parsedValue && sameDay(date, parsedValue);
                const isToday = sameDay(date, stripTime(new Date()));
                const outMonth = !inMonth;
                return (
                  <button
                    key={`${date.toISOString()}-${i}`}
                    type="button"
                    className={[
                      "sdf-cell",
                      outMonth ? "is-out" : "",
                      isToday ? "is-today" : "",
                      isSelected ? "is-selected" : "",
                      dis ? "is-disabled-date" : "",
                      weekend ? "is-weekend" : "",
                    ].join(" ")}
                    disabled={dis}
                    onMouseEnter={() => !dis && setActiveDate(date)}
                    onClick={() => !dis && commit(date)}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="sdf-foot">
              <button
                type="button"
                className="sdf-today"
                onClick={() => {
                  const t = stripTime(new Date());
                  if (disableWeekends && (t.getDay() === 0 || t.getDay() === 6))
                    return;
                  setViewDate(new Date(t.getFullYear(), t.getMonth(), 1));
                  setActiveDate(t);
                  commit(t);
                }}
              >
                Today
              </button>
              <button
                type="button"
                className="sdf-clear"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {(helperText || error) && (
        <div className={["sdf-helper", error ? "is-error" : ""].join(" ")}>
          {error || helperText}
        </div>
      )}
    </div>
  );
}
